import { DateTime } from 'luxon';
import { CoreItem, CoreItemBackend } from '../core-item/core-item';
import { filterWordList } from '../natural-language/fillter-word-list';
import { stopWordList } from '../natural-language/stop-word-list';

export interface SessionMessageBackend extends CoreItemBackend {
  parentId: string; // session Id
  subData: SessionMessageSubData;
  publicSubData?: SessionMessagePublicSubData;
}

export interface SessionMessage extends CoreItem {
  parentId: string;
  subData: SessionMessageSubData;
  publicSubData?: SessionMessagePublicSubData;
}

export interface SessionMessagePublicSubData {
  salesHumanFeedbackList?: SessionMessageSalesHumanFeedback[];
}

// Talk/Listen Ratio, Filler Words, Talk Speed, Longest Monologue.
// Give assessment on the potential buy success probability of the virtual customer. Give an suggestion of what should the Sales rep should respond as a top sales. Point out which stage of the conversation should be according to the sales methodology.
export interface SessionMessageSubData {
  salesMessage: string;
  customerMessage: string;

  objectionId?: string;
  objectionSuggestions?: string;
  objectionScore?: number;
  objectionImprovedResponse?: string;
  objectionMissingInformationList?: { missingInformation: string; reasoning: string }[];
  objectionInaccurateInformationList?: { inaccurateInformation: string; reasoning: string }[];

  customerAudioUrl?: string;

  salesAiFeedback?: SessionMessageSalesAiFeedback;

  salesStartTimeStr: string;
  salesEndTimeStr: string;

  salesSeconds: number;

  customerStartTimeStr: string;
  customerEndTimeStr: string;

  customerSeconds: number;
}

export interface SessionMessageSalesAiFeedback {
  rewrittenResponse: string;
  improvementSuggestions: string[];
}

export interface SessionMessageSalesHumanFeedback {
  createdById: string;
  createdTimeStr: string;
  feedback: string;
}

export class SessionMessageUtils {
  static getConversationMins(messageList: SessionMessage[]) {
    if (messageList.length === 0) {
      return 0;
    }
    const startTime = DateTime.fromISO(messageList[0].subData.salesStartTimeStr);

    const endTime = DateTime.fromISO(messageList[messageList.length - 1].subData.customerEndTimeStr);

    return Math.round(endTime.diff(startTime, 'minutes').minutes);
  }

  static removeWordsFromString(totalText: string, wordsToRemove: string[]): string {
    // Create a regular expression pattern to match each word to be removed
    const pattern = new RegExp(`\\b(${wordsToRemove.join('|')})\\b`, 'gi');
    // Replace matched words with an empty string
    return totalText.replace(pattern, '').replace(/\s+/g, ' ').trim();
  }

  static getTalkSpeedPerMin(messageList: SessionMessage[]) {
    const totalMins = this.getTotalSeconds(messageList).salesSeconds / 60;
    const totalSalesMessage = this.getTotalSalesMessage(messageList);
    const totalCount = (totalSalesMessage ?? '').split(' ').length;

    if (totalMins === 0) {
      return 0;
    }
    return Math.round(totalCount / totalMins);
  }

  static getFillerWordsPerMin(messageList: SessionMessage[]) {
    const totalSalesMessage = this.getTotalSalesMessage(messageList);
    const wordCounts = this.countWordsInString(totalSalesMessage, filterWordList);
    const totalCount = Object.values(wordCounts).reduce((sum, count) => sum + count, 0);

    const totalMins = this.getTotalSeconds(messageList).salesSeconds / 60;

    if (totalMins === 0) {
      return 0;
    }

    return Math.round(totalCount / totalMins);
  }

  static countWordsInString(totalText: string, wordsToCount: string[]): { [key: string]: number } {
    // Initialize an object to store the counts of each word
    const wordCounts: { [key: string]: number } = {};

    // Create a regular expression pattern to match each word to be counted
    wordsToCount.forEach((word) => {
      const pattern = new RegExp(`\\b${word}\\b`, 'gi');
      const matches = totalText.match(pattern);
      wordCounts[word] = matches ? matches.length : 0;
    });

    return wordCounts;
  }

  static getTotalSalesMessage(messageList: SessionMessage[]) {
    let totalText = '';

    messageList.forEach((c) => (totalText += c.subData.salesMessage));

    return this.removeWordsFromString(totalText, stopWordList);
  }

  static getTotalSeconds(messageList: SessionMessage[]) {
    let salesSeconds = 0;
    let customerSeconds = 0;

    messageList.forEach((message) => {
      salesSeconds += message.subData.salesSeconds;
      customerSeconds += message.subData.customerSeconds;
    });

    return {
      salesSeconds,
      customerSeconds,
    };
  }

  static getLongestMonologueSeconds(messageList: SessionMessage[]) {
    let longest = 0;
    messageList.forEach((message) => {
      longest = Math.max(message.subData.salesSeconds, longest);
    });
    return longest;
  }

  static getTalkListRatio(messageList: SessionMessage[]) {
    const totalSeconds = this.getTotalSeconds(messageList);
    const ratio = this.calculatePercentageRatio(totalSeconds.salesSeconds, totalSeconds.customerSeconds);

    return ratio;
  }

  static calculatePercentageRatio(a: number, b: number): { percentageA: number; percentageB: number } {
    const total = a + b;
    const percentageA = Math.round((a / total) * 100);
    const percentageB = Math.round((b / total) * 100);
    return { percentageA, percentageB };
  }

  static convertSecondsToMinutesSeconds(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
}
